import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Unplug, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PlatformIcon, getPlatformMeta } from "@/components/PlatformIcon";
import { StepShell } from "./WizardUI";
import { SOCIAL_PLATFORMS } from "./constants";

export default function Step7Social({ onSave, onBack, onSkip, busy }) {
  const [status, setStatus] = useState({});
  const [wp, setWp] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/social/status"),
        api.get("/blog-connector/wordpress/connection"),
      ]);
      const map = {};
      (a.data.platforms || []).forEach((p) => { map[p.platform] = p; });
      setStatus(map);
      setWp(b.data);
    } catch { toast.error("Couldn't load connection status"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const disconnect = async (platform) => {
    try {
      await api.post(`/social/${platform}/disconnect`);
      toast.success("Disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  const cardFor = (p) => {
    const isWp = p.key === "wordpress";
    const isWa = p.key === "whatsapp";
    const conn = isWp ? !!wp?.connected : !!status[p.key]?.connected;
    const label = isWp ? (wp?.connection?.site_url || "") : (status[p.key]?.account_label || "");
    const connectHref = isWp ? "/dashboard/blog-connector" : "/dashboard/social-connections";
    return (
      <div key={p.key} data-testid={`onb-social-card-${p.key}`}
        className="rounded-xl border border-line bg-bg-surface p-4 flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          {getPlatformMeta(p.key)
            ? <PlatformIcon platform={p.key} className="h-5 w-5" />
            : <span className="h-5 w-5 rounded bg-bg-elevated border border-line flex items-center justify-center text-[9px] font-bold text-ink-secondary">W</span>}
          <span className="text-sm text-ink-primary font-semibold">{p.label}</span>
        </div>
        <div className={`font-mono text-[9px] uppercase tracking-wider ${conn ? "text-emerald-300" : "text-ink-muted"}`}
          data-testid={`onb-social-status-${p.key}`}>
          {isWa && !conn ? "○ Coming soon" : conn ? "● Connected" : "○ Not connected"}
        </div>
        {conn && label && <div className="text-[11px] text-ink-secondary truncate">{label}</div>}
        {conn && !isWp && status[p.key]?.connected_at && (
          <div className="text-[10px] text-ink-muted">Authorized {new Date(status[p.key].connected_at).toLocaleDateString()} · posting permissions granted</div>
        )}
        <div className="mt-auto flex items-center gap-2 pt-1">
          {!conn && !isWa && (
            <a href={connectHref} target="_blank" rel="noreferrer" data-testid={`onb-social-connect-${p.key}`}
              className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 text-[11px] font-semibold text-black hover:bg-brand-amber/90 transition">
              Connect <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {conn && !isWp && (
            <button type="button" onClick={() => disconnect(p.key)} data-testid={`onb-social-disconnect-${p.key}`}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-rose-300 transition">
              <Unplug className="h-3 w-3" /> Disconnect
            </button>
          )}
          {conn && isWp && (
            <a href="/dashboard/blog-connector" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition">
              Manage <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    );
  };

  const connectedCount = SOCIAL_PLATFORMS.filter((p) =>
    p.key === "wordpress" ? wp?.connected : status[p.key]?.connected).length;

  return (
    <StepShell step={7} title="Social-media connections"
      subtitle="Connect the platforms you post to. Connections open in a new tab — come back and refresh when done."
      onBack={onBack} onContinue={() => onSave({ connected_count: connectedCount })}
      onSkip={onSkip} busy={busy} skipLabel="Connect accounts later">
      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-secondary">{connectedCount} of {SOCIAL_PLATFORMS.length} connected</div>
        <button type="button" onClick={load} disabled={loading} data-testid="onb-social-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh status
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SOCIAL_PLATFORMS.map(cardFor)}
      </div>
    </StepShell>
  );
}
